import { useState } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
// Hooks
import { useBreakpoints, useOnboardingContext } from 'hooks';
// Constants
import {
  ACCOUNT_PRODUCTS,
  MANAGE_MERCH,
  UPLOAD_NEW_MEDIA,
  USER_SCHEDULE_INTERVIEW,
} from 'constants/routes';
// Ui
import Modal from 'ui3/Modal/Modal';
import Button from 'ui3/Button/Button';
import MillionsIcon, { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Components
import CreateEditGoLiveStream from 'components/ManageStreams/CreateEditStream/CreateEditGoLiveStream';
// Styles
import styles from './CreateMenu.module.scss';

type CreateTile = {
  key: string;
  label: string;
  description: string;
  icon: IconNames;
  colorClass: string;
  onClick: () => void;
};

const CreateMenu = () => {
  const router = useRouter();
  const { isBreakpointL } = useBreakpoints();
  const { setShowCreateStreamModal } = useOnboardingContext();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isCreateStreamOpen, setIsCreateStreamOpen] = useState<boolean>(false);

  const closeMenu = () => setIsMenuOpen(false);
  const handleCloseCreateStream = () => setIsCreateStreamOpen(false);

  const goTo = (path: string) => {
    closeMenu();
    router.push(path);
  };

  const openCreateStream = () => {
    closeMenu();
    setIsCreateStreamOpen(true);
  };

  const openScheduleStream = () => {
    closeMenu();
    setShowCreateStreamModal(true);
  };

  const tiles: CreateTile[] = [
    {
      key: 'go-live',
      label: 'Go Live',
      description: 'Go live. Get paid.',
      icon: 'stream',
      colorClass: styles.iconGoLive,
      onClick: openCreateStream,
    },
    {
      key: 'schedule',
      label: 'Schedule Stream',
      description: 'Schedule a PPV or Stream.',
      icon: 'videoCamera',
      colorClass: styles.iconSchedule,
      onClick: openScheduleStream,
    },
    {
      key: 'interview',
      label: 'Interview',
      description: 'Two voices, one moment.',
      icon: 'microphoneOnOutline',
      colorClass: styles.iconInterview,
      onClick: () => goTo(USER_SCHEDULE_INTERVIEW),
    },
    {
      key: 'upload',
      label: 'Upload Media',
      description: 'Drop content. Build a following.',
      icon: 'movie',
      colorClass: styles.iconUpload,
      onClick: () => goTo(UPLOAD_NEW_MEDIA),
    },
    {
      key: 'merch',
      label: 'Merch',
      description: 'Wear the brand. Be the brand.',
      icon: 'shirtOutline',
      colorClass: styles.iconMerch,
      onClick: () => goTo(MANAGE_MERCH),
    },
    {
      key: 'memorabilia',
      label: 'Memorabilia',
      description: 'Every piece tells your story.',
      icon: 'trophy',
      colorClass: styles.iconMemorabilia,
      onClick: () => goTo(`${ACCOUNT_PRODUCTS}/create`),
    },
  ];

  return (
    <>
      <Button
        variant="secondary"
        size="medium"
        onClick={() => setIsMenuOpen(true)}
        aria-haspopup="dialog"
      >
        <MillionsIcon name="plus" className={styles.triggerIcon} />
        Create
      </Button>

      <Modal
        open={isMenuOpen}
        onClose={closeMenu}
        title="Create"
        subTitle="Post content, earn, and go viral."
        titleAlign="center"
        size="large"
        blockScroll={isBreakpointL}
        rootClassName={styles.modalRoot}
      >
        <div className={styles.grid}>
          {tiles.map((tile) => (
            <button
              key={tile.key}
              type="button"
              className={styles.tile}
              onClick={tile.onClick}
            >
              <span className={cn(styles.iconCircle, tile.colorClass)}>
                <MillionsIcon name={tile.icon} className={styles.tileIcon} />
              </span>
              <span className={styles.tileLabel}>{tile.label}</span>
              <span className={styles.tileDescription}>{tile.description}</span>
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={isCreateStreamOpen}
        onClose={handleCloseCreateStream}
        title="Stream Details"
        titleAlign="left"
        size="large"
        fullPageOnMobile
        blockScroll={isBreakpointL}
        containerClassName={styles.createStreamModalContainer}
      >
        <CreateEditGoLiveStream onComplete={handleCloseCreateStream} />
      </Modal>
    </>
  );
};

export default CreateMenu;
